import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Landmark,
  Mail,
  MessageSquare,
  Pencil,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  AUDIT_ACTION_LABELS,
  REFUND_REASON_LABELS,
  type AdminOrderDetail,
  type AdminPaymentQueryResponse,
  type AuditAction,
  type CurrencyCode,
} from "@ticketnjoy/types";

import {
  ConfirmPaymentDialog,
  DismissPaymentIssueDialog,
  OrderBuyerDialog,
  OrderCancelDialog,
  OrderRefundDialog,
  RefundPaymentDialog,
} from "@/components/order-action-dialogs";
import { PlatformLogRow } from "@/components/platform-log-row";
import { FullPageSpinner } from "@/components/require-admin";
import { QueryError } from "@/components/query-error";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useIsSuperAdmin,
  useOrderQuery,
  usePaymentQueryMutation,
  useResendOrderNotificationMutation,
} from "@/features/api";
import { ApiError, showApiError } from "@/lib/api-client";
import {
  ORDER_CHANNEL_LABELS,
  ORDER_STATUS_LABELS,
  ORGANIZER_STATUS_LABELS,
  PAYMENT_ISSUE_RESOLUTION_LABELS,
  PAYMENT_PROVIDER_LABELS,
  PAYMENT_QUERY_STATUS_LABELS,
  PLATFORM_ACTION_LABELS,
  dateTime,
  money,
  orderStatusVariant,
  organizerStatusVariant,
} from "@/lib/format";

const TICKET_STATUS_LABELS: Record<string, string> = {
  Valid: "Geçerli",
  Used: "Kullanıldı",
  Cancelled: "İptal",
};

export function OrderDetailPage() {
  const { orderNumber = "" } = useParams();
  const { data, isLoading, error, refetch, isRefetching } = useOrderQuery(orderNumber);

  if (isLoading) return <FullPageSpinner />;
  if (error || !data) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4">
        <BackLink />
        {notFound ? (
          <p className="text-sm text-destructive">Sipariş bulunamadı.</p>
        ) : (
          <QueryError message="Sipariş yüklenemedi." onRetry={() => refetch()} retrying={isRefetching} />
        )}
      </div>
    );
  }

  const { order, payment } = data;
  const showPaymentIssue =
    payment.attention || payment.recoverable || payment.resolution !== null || payment.manualConfirmation !== null;

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">{order.orderNumber}</h1>
          <Badge variant={orderStatusVariant(order.status)}>
            {ORDER_STATUS_LABELS[order.status] ?? order.status}
          </Badge>
          {payment.attention && <Badge variant="destructive">Sorunlu ödeme</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">
          <Link to={`/organizers/${data.organizerId}`} className="text-foreground hover:underline">
            {data.organizerName}
          </Link>{" "}
          · {order.eventTitle} · {dateTime(order.eventStartsAt)} · {order.shopName} (
          {ORDER_CHANNEL_LABELS[data.channel] ?? data.channel})
        </p>
      </div>

      {order.status === "Pending" && (
        <Notice tone="warning">
          Ödeme bekleniyor. Süre {dateTime(data.expiresAt)} tarihinde doluyor; dolunca ayrılan biletler
          satışa döner.
        </Notice>
      )}
      {(order.status === "Cancelled" || order.status === "Expired") && (
        <Notice tone="muted">
          {order.status === "Cancelled" ? "İptal edildi" : "Ödeme süresi doldu"}
          {data.cancelledAt && ` (${dateTime(data.cancelledAt)})`}
          {data.cancellationReason && ` · Sebep: ${cancellationReasonLabel(data.cancellationReason)}`}
          {order.lastPaymentError && ` · Banka yanıtı: ${order.lastPaymentError}`}
        </Notice>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {showPaymentIssue && <PaymentIssueCard detail={data} />}
          <TicketsCard detail={data} />
          <PaymentCard detail={data} />
          {order.refunds.length > 0 && <RefundsCard detail={data} />}
          <ActivityCard detail={data} />
        </div>
        <div className="min-w-0 space-y-6">
          <BuyerCard detail={data} />
          <ActionsCard detail={data} />
          <OrganizerCard detail={data} />
        </div>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/orders"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Siparişler
    </Link>
  );
}

function Notice({ tone, children }: { tone: "warning" | "muted"; children: ReactNode }) {
  return (
    <div
      className={
        tone === "warning"
          ? "rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm"
          : "rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground"
      }
    >
      {children}
    </div>
  );
}

/**
 * Banka "ödendi" dedi ama sipariş tamamlanamadı, ya da dönüş hiç gelmedi. Süper admin bankaya
 * sorar; sonuca göre onaylar (biletler kesilir), parayı biletsiz iade eder veya kapatır.
 */
function PaymentIssueCard({ detail }: { detail: AdminOrderDetail }) {
  const { order, payment } = detail;
  const cur = order.currency as CurrencyCode;
  const isSuper = useIsSuperAdmin();
  const queryPayment = usePaymentQueryMutation(order.orderNumber);
  const [query, setQuery] = useState<AdminPaymentQueryResponse | null>(null);
  const [dialog, setDialog] = useState<"confirm" | "refund" | "dismiss" | null>(null);
  const provider = payment.provider ? (PAYMENT_PROVIDER_LABELS[payment.provider] ?? payment.provider) : null;

  async function onQuery() {
    try {
      setQuery(await queryPayment.mutateAsync());
    } catch (err) {
      showApiError(err, "Bankaya sorulamadı.");
    }
  }

  const closeDialog = (open: boolean) => {
    if (!open) setDialog(null);
  };

  return (
    <Card className={payment.attention ? "border-destructive/50" : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {payment.resolution ? (
            <CheckCircle2 className="size-4 text-muted-foreground" />
          ) : (
            <AlertTriangle className="size-4 text-destructive" />
          )}
          {payment.resolution ? "Ödeme sorunu kapatıldı" : "Ödeme sorunu"}
        </CardTitle>
        <CardDescription>
          {payment.issue === "captured_not_finalized"
            ? `Banka ödemenin alındığını bildirdi${payment.issueAt ? ` (${dateTime(payment.issueAt)})` : ""} ama sipariş tamamlanamadı; biletler kesilmedi.`
            : order.status === "Cancelled"
              ? "Banka ödemeyi başarısız bildirdi. Karttan çekim yapıldığından şüpheleniliyorsa bankaya sor."
              : `Ödeme ${provider ?? "kartla"} başlatıldı, bankadan dönüş gelmedi ve süre doldu. Alıcı banka sayfasını kapatmış ya da dönüş kaybolmuş olabilir.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="space-y-2">
          <Row label="Beklenen tutar" value={money(payment.expectedAmount, cur)} />
          {payment.transactionId && (
            <Row label="Banka işlem no" value={<span className="font-mono text-xs">{payment.transactionId}</span>} />
          )}
          {payment.resolution && (
            <Row
              label="Sonuç"
              value={`${PAYMENT_ISSUE_RESOLUTION_LABELS[payment.resolution]}${payment.resolvedAt ? ` · ${dateTime(payment.resolvedAt)}` : ""}`}
            />
          )}
          {payment.manualConfirmation && (
            <Row
              label="Elle onay"
              value={
                payment.manualConfirmation === "provider_query"
                  ? "Banka doğruladı"
                  : "Banka doğrulamadan, sebeple (Kayıtlar'da)"
              }
            />
          )}
        </div>

        {query && <PaymentQueryResult query={query} currency={cur} />}

        {payment.recoverable &&
          (isSuper ? (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              <Button variant="outline" onClick={onQuery} disabled={queryPayment.isPending}>
                <Landmark className="size-4" />
                {queryPayment.isPending ? "Soruluyor..." : query ? "Bankaya yeniden sor" : "Bankaya sor"}
              </Button>
              <Button onClick={() => setDialog("confirm")}>
                <CheckCircle2 className="size-4" />
                Onayla, biletleri kes
              </Button>
              <Button variant="destructive" onClick={() => setDialog("refund")}>
                <RotateCcw className="size-4" />
                Parayı iade et
              </Button>
              {payment.issue === null && (
                <Button variant="ghost" onClick={() => setDialog("dismiss")}>
                  <XCircle className="size-4" />
                  Ödeme yok, kapat
                </Button>
              )}
            </div>
          ) : (
            <p className="border-t pt-3 text-xs text-muted-foreground">
              Bankaya sorma, onay ve iade için süper admin yetkisi gerekir.
            </p>
          ))}
      </CardContent>
      {isSuper && payment.recoverable && (
        <>
          <ConfirmPaymentDialog detail={detail} query={query} open={dialog === "confirm"} onOpenChange={closeDialog} />
          <RefundPaymentDialog detail={detail} query={query} open={dialog === "refund"} onOpenChange={closeDialog} />
          <DismissPaymentIssueDialog
            detail={detail}
            query={query}
            open={dialog === "dismiss"}
            onOpenChange={closeDialog}
          />
        </>
      )}
    </Card>
  );
}

function PaymentQueryResult({ query, currency }: { query: AdminPaymentQueryResponse; currency: CurrencyCode }) {
  const paid = query.status === "Paid";
  return (
    <div
      className={
        paid && query.amountMatches
          ? "space-y-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3"
          : "space-y-2 rounded-md border bg-muted/40 p-3"
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">Banka: {PAYMENT_QUERY_STATUS_LABELS[query.status]}</span>
        <span className="text-xs text-muted-foreground">{dateTime(query.queriedAt)}</span>
      </div>
      {query.amount !== null && (
        <Row
          label="Bankadaki tutar"
          value={`${money(query.amount, currency)}${query.amountMatches ? "" : ` · beklenen ${money(query.expectedAmount, currency)}`}`}
        />
      )}
      {query.refundedAmount !== null && query.refundedAmount > 0 && (
        <Row label="Bankada iade edilen" value={money(query.refundedAmount, currency)} />
      )}
      {query.transactionId && (
        <Row label="İşlem no" value={<span className="font-mono text-xs">{query.transactionId}</span>} />
      )}
      {query.providerStatus && <Row label="Banka durum kodu" value={query.providerStatus} />}
      {query.message && <p className="text-xs text-muted-foreground">{query.message}</p>}
      {paid && !query.amountMatches && (
        <p className="text-xs text-destructive">Tutar beklenenle aynı değil; onay sebep ister.</p>
      )}
    </div>
  );
}

function cancellationReasonLabel(reason: string): string {
  return reason === "payment_failed" ? "Ödeme başarısız" : reason;
}

function Row({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? "font-semibold tabular-nums" : "text-right tabular-nums"}>{value}</span>
    </div>
  );
}

function TicketsCard({ detail }: { detail: AdminOrderDetail }) {
  const { order } = detail;
  const checkIns = new Map(detail.checkIns.map((c) => [c.ticketId, c]));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Biletler</CardTitle>
        <CardDescription>
          {order.items
            .map((i) => `${i.quantity} × ${i.ticketTypeName}${i.isFree ? " (hediye)" : ""}`)
            .join(" · ")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {order.tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Bilet yok; biletler ödeme tamamlanınca üretilir.
          </p>
        ) : (
          <ul className="divide-y text-sm">
            {order.tickets.map((t) => {
              const checkIn = checkIns.get(t.id);
              return (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div>
                    <div className="font-mono text-xs">{t.ticketCode}</div>
                    <div className="text-xs text-muted-foreground">{t.ticketTypeName}</div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={
                        t.status === "Valid" ? "success" : t.status === "Used" ? "secondary" : "outline"
                      }
                    >
                      {TICKET_STATUS_LABELS[t.status] ?? t.status}
                    </Badge>
                    {checkIn && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {dateTime(checkIn.usedAt)}
                        {checkIn.checkedInByEmail && ` · ${checkIn.checkedInByEmail}`}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentCard({ detail }: { detail: AdminOrderDetail }) {
  const { order } = detail;
  const cur = order.currency as CurrencyCode;
  const provider = order.payment?.provider;
  const isPaid = order.paidAt !== null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ödeme</CardTitle>
        <CardDescription>
          {provider ? (PAYMENT_PROVIDER_LABELS[provider] ?? provider) : "Ödeme başlatılmadı"}
          {order.paidAt && ` · ${dateTime(order.paidAt)}`}
          {` · Oluşturma ${dateTime(order.createdAt)}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Bilet tutarı" value={money(order.subtotal, cur)} />
        {order.discountTotal > 0 && <Row label="İndirim" value={`−${money(order.discountTotal, cur)}`} />}
        <Row label="KDV" value={money(order.taxTotal, cur)} />
        <Row label="Servis ücreti" value={money(order.serviceFeeTotal, cur)} />
        <Row label="Alıcının ödediği" value={money(order.total, cur)} strong />
        {order.refundedAmount > 0 && (
          <Row label="İade edilen" value={`−${money(order.refundedAmount, cur)}`} />
        )}
        {isPaid && (
          <div className="space-y-2 border-t pt-2">
            <Row label="Komisyon (satış anı)" value={money(detail.commissionTotal, cur)} />
            <Row label="Kart komisyonu (satış anı)" value={money(detail.cardFeeTotal, cur)} />
            <Row label="Organizatör neti (satış anı)" value={money(detail.organizerNetTotal, cur)} />
            {order.refundedAmount > 0 && order.total > 0 && (
              <Row
                label="İade sonrası organizatör neti"
                value={money(
                  (detail.organizerNetTotal * (order.total - order.refundedAmount)) / order.total,
                  cur,
                )}
              />
            )}
          </div>
        )}
        {order.payment?.transactionId && (
          <div className="border-t pt-2">
            <Row label="İşlem no" value={<span className="font-mono text-xs">{order.payment.transactionId}</span>} />
            {order.payment.installmentCount > 1 && (
              <Row label="Taksit" value={`${order.payment.installmentCount} taksit`} />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RefundsCard({ detail }: { detail: AdminOrderDetail }) {
  const { order } = detail;
  return (
    <Card>
      <CardHeader>
        <CardTitle>İadeler</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y text-sm">
          {order.refunds.map((r) => (
            <li key={r.id} className="py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium tabular-nums">{money(r.amount, order.currency as CurrencyCode)}</span>
                <span className="text-xs text-muted-foreground">{dateTime(r.createdAt)}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {r.refundedTicketCount} bilet · {REFUND_REASON_LABELS[r.reason] ?? r.reason}
                {r.reasonNote && ` · ${r.reasonNote}`} · {r.actorEmail ?? "Silinmiş üye"}
                {r.providerRefundId && ` · Ref ${r.providerRefundId}`}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ActivityCard({ detail }: { detail: AdminOrderDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Aktivite</CardTitle>
        <CardDescription>
          Organizatörün Aktivite kaydındaki satırlar. Admin işlemleri organizatöre "TicketNjoy" olarak görünür.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {detail.activity.length === 0 ? (
          <p className="text-sm text-muted-foreground">Kayıt yok.</p>
        ) : (
          <ul className="divide-y text-sm">
            {detail.activity.map((a) => (
              <PlatformLogRow
                key={`${a.createdAt}-${a.action}`}
                action={a.action}
                label={
                  AUDIT_ACTION_LABELS[a.action as AuditAction] ?? PLATFORM_ACTION_LABELS[a.action]
                }
                actorEmail={
                  a.adminEmail
                    ? `${a.actorEmail ?? "TicketNjoy"} (${a.adminEmail})`
                    : (a.actorEmail ?? "Silinmiş üye")
                }
                diff={a.diff}
                createdAt={a.createdAt}
                hiddenDiffKeys={["orderNumber"]}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function BuyerCard({ detail }: { detail: AdminOrderDetail }) {
  const { order } = detail;
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Alıcı</CardTitle>
        <CardAction>
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Pencil className="size-4" />
            Düzelt
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <div className="font-medium">
          {order.buyerFirstName} {order.buyerLastName}
        </div>
        <div className="break-all text-muted-foreground">{order.buyerEmail}</div>
        <div className="text-muted-foreground">{order.buyerPhone || "Telefon yok"}</div>
        <Link
          to={`/orders?q=${encodeURIComponent(order.buyerEmail)}`}
          className="inline-block pt-2 text-xs text-muted-foreground hover:text-foreground"
        >
          Bu alıcının diğer siparişleri
        </Link>
      </CardContent>
      <OrderBuyerDialog detail={detail} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function ActionsCard({ detail }: { detail: AdminOrderDetail }) {
  const { order } = detail;
  const isSuper = useIsSuperAdmin();
  const resend = useResendOrderNotificationMutation(order.orderNumber);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const canResend = order.status === "Paid";
  const canRefund =
    (order.status === "Paid" || order.status === "PartiallyRefunded") &&
    order.tickets.some((t) => t.status === "Valid");
  const canCancel = order.status === "Pending";

  async function onResend(channel: "email" | "sms") {
    try {
      await resend.mutateAsync(channel);
      toast.success(channel === "email" ? "Onay e-postası gönderildi." : "Onay SMS'i gönderildi.");
    } catch (err) {
      showApiError(err, "Gönderilemedi.");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>İşlemler</CardTitle>
        <CardDescription>
          Organizatör adına yapılır; organizatör Aktivite kaydında "TicketNjoy" olarak görür.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Button
            variant="outline"
            className="w-full"
            disabled={!canResend || resend.isPending}
            onClick={() => onResend("email")}
          >
            <Mail className="size-4" />
            Onay e-postasını tekrar gönder
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={!canResend || !order.buyerPhone || resend.isPending}
            onClick={() => onResend("sms")}
          >
            <MessageSquare className="size-4" />
            Onay SMS'ini tekrar gönder
          </Button>
          {!canResend && (
            <p className="text-xs text-muted-foreground">Yalnız ödenmiş siparişlerde gönderilir.</p>
          )}
        </div>

        <div className="space-y-2 border-t pt-3">
          {!isSuper ? (
            <p className="text-xs text-muted-foreground">
              İade ve iptal için süper admin yetkisi gerekir.
            </p>
          ) : (
            <>
              <Button
                variant="destructive"
                className="w-full"
                disabled={!canRefund}
                onClick={() => setRefundOpen(true)}
              >
                <RotateCcw className="size-4" />
                İade et
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={!canCancel}
                onClick={() => setCancelOpen(true)}
              >
                <Ban className="size-4" />
                Bekleyen siparişi iptal et
              </Button>
            </>
          )}
        </div>
      </CardContent>
      {isSuper && (
        <>
          <OrderRefundDialog detail={detail} open={refundOpen} onOpenChange={setRefundOpen} />
          <OrderCancelDialog detail={detail} open={cancelOpen} onOpenChange={setCancelOpen} />
        </>
      )}
    </Card>
  );
}

function OrganizerCard({ detail }: { detail: AdminOrderDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Organizatör</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-2">
          <Link to={`/organizers/${detail.organizerId}`} className="font-medium hover:underline">
            {detail.organizerName}
          </Link>
          <Badge variant={organizerStatusVariant(detail.organizerStatus)}>
            {ORGANIZER_STATUS_LABELS[detail.organizerStatus] ?? detail.organizerStatus}
          </Badge>
        </div>
        <div className="flex flex-col gap-1 text-xs">
          <Link
            to={`/orders?organizerId=${detail.organizerId}&eventId=${detail.eventId}`}
            className="text-muted-foreground hover:text-foreground"
          >
            Bu etkinliğin siparişleri
          </Link>
          <Link
            to={`/orders?organizerId=${detail.organizerId}`}
            className="text-muted-foreground hover:text-foreground"
          >
            Organizatörün bütün siparişleri
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
